import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Badge, Row, Col, Spinner, Pagination } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUserPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import EnrollmentService from '../../services/enrollment.service';
import StudentService from '../../services/student.service';
import ClassroomService from '../../services/classroom.service';
import { useAcademicYear } from '../../contexts/AcademicYearContext';

const Enrollments = () => {
  const { selectedYear } = useAcademicYear();
  const [enrollments, setEnrollments] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    totalPages: 1
  });

  // Estados do formulário
  const [studentSearch, setStudentSearch] = useState('');
  const [student, setStudent] = useState(null);
  const [studentOptions, setStudentOptions] = useState([]);
  const [searchingStudent, setSearchingStudent] = useState(false);
  const [classroomId, setClassroomId] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());

  // Filtros
  const [filters, setFilters] = useState({
    classroom: '',
    student_cpf: '',
    student_name: ''
  });

  // Estados de mensagem
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });

  useEffect(() => {
    fetchEnrollments(1);
    fetchClassrooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEnrollments(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedYear]);

  const fetchEnrollments = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, year: selectedYear };

      // Adicionar filtros aos params
      if (filters.classroom) params.classroom = filters.classroom;
      if (filters.student_cpf) params.student_cpf = filters.student_cpf.replace(/\D/g, '');
      if (filters.student_name) params.student_name = filters.student_name;

      const data = await EnrollmentService.getAll(params);
      setEnrollments(data.results || data);
      setPagination({
        count: data.count || 0,
        next: data.next,
        previous: data.previous,
        currentPage: page,
        totalPages: Math.ceil((data.count || 0) / 10)
      });
    } catch (error) {
      showAlert('Erro ao carregar matrículas.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const data = await ClassroomService.getAll({ active: true });
      setClassrooms(data.results || data);
    } catch (error) {
      showAlert('Erro ao carregar turmas.', 'danger');
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 5000);
  };

  const handleSearchStudent = async () => {
    const query = studentSearch.trim();
    if (!query) {
      showAlert('Digite o CPF ou o nome do aluno.', 'warning');
      return;
    }

    const digits = query.replace(/\D/g, '');
    const hasLetters = /[a-zA-ZÀ-ÿ]/.test(query);
    const params = {};

    if (digits && !hasLetters) {
      params.cpf = digits;
    } else {
      params.name = query;
    }

    setSearchingStudent(true);
    try {
      const data = await StudentService.getWithoutEnrollment(params);
      const results = data.results || data || [];

      if (results.length === 1) {
        setStudent(results[0]);
        setStudentOptions([]);
        showAlert(`Aluno encontrado: ${results[0].name}`, 'success');
      } else if (results.length > 1) {
        setStudent(null);
        setStudentOptions(results);
        showAlert(`${results.length} alunos encontrados. Selecione um.`, 'info');
      } else {
        setStudent(null);
        setStudentOptions([]);
        showAlert('Aluno não encontrado ou já possui matrícula ativa.', 'warning');
      }
    } catch (error) {
      showAlert('Erro ao buscar aluno.', 'danger');
      setStudent(null);
      setStudentOptions([]);
    } finally {
      setSearchingStudent(false);
    }
  };

  const handleOpenModal = (mode = 'create', enrollment = null) => {
    setModalMode(mode);
    setSelectedEnrollment(enrollment);

    if (mode === 'create') {
      resetForm();
    } else {
      // Modo edição
      setStudentSearch(enrollment.student_cpf || enrollment.student_name || '');
      setStudent({ id: enrollment.student, name: enrollment.student_name });
      setStudentOptions([]);
      setClassroomId(enrollment.classroom);
      setYear(enrollment.year);
    }

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const resetForm = () => {
    setStudentSearch('');
    setStudent(null);
    setStudentOptions([]);
    setClassroomId('');
    setYear(new Date().getFullYear());
    setSelectedEnrollment(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!student) {
      showAlert('Busque um aluno pelo CPF ou nome primeiro.', 'warning');
      return;
    }

    if (!classroomId) {
      showAlert('Selecione uma turma.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const enrollmentData = {
        student: student.id,
        classroom: classroomId,
        year: parseInt(year),
        school: student.school
      };

      if (modalMode === 'create') {
        await EnrollmentService.create(enrollmentData);
        showAlert('Matrícula realizada com sucesso!', 'success');
      } else {
        await EnrollmentService.update(selectedEnrollment.id, enrollmentData);
        showAlert('Matrícula atualizada com sucesso!', 'success');
      }

      fetchEnrollments(pagination.currentPage);
      handleCloseModal();
    } catch (error) {
      // Extrair mensagens de erro do backend
      let errorMessage = 'Erro ao processar matrícula.';
      if (error.response?.data) {
        const errors = error.response.data;
        const errorMessages = [];

        Object.keys(errors).forEach(key => {
          if (Array.isArray(errors[key])) {
            errorMessages.push(`${key}: ${errors[key].join(', ')}`);
          } else {
            errorMessages.push(`${key}: ${errors[key]}`);
          }
        });

        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(' | ');
        }
      }
      showAlert(errorMessage, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta matrícula?')) return;

    setLoading(true);
    try {
      await EnrollmentService.delete(id);
      showAlert('Matrícula excluída com sucesso!', 'success');
      fetchEnrollments(pagination.currentPage);
    } catch (error) {
      showAlert('Erro ao excluir matrícula.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const formatCpf = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      .slice(0, 14);
  };

  const handleStudentSearchChange = (e) => {
    const value = e.target.value;
    const hasLetters = /[a-zA-ZÀ-ÿ]/.test(value);
    setStudentSearch(hasLetters ? value : formatCpf(value));
  };

  const handlePageChange = (page) => {
    fetchEnrollments(page);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      classroom: '',
      student_cpf: '',
      student_name: ''
    });
  };

  const getFullClassroomName = (enrollment) => {
    // Usar o campo classroom_full_name que já vem formatado do backend
    return enrollment.classroom_full_name || enrollment.classroom_name;
  };

  return (
    <>
      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Turma</Form.Label>
                <Form.Select
                  name="classroom"
                  value={filters.classroom}
                  onChange={handleFilterChange}
                >
                  <option value="">Todas as turmas</option>
                  {classrooms.map((classroom) => (
                    <option key={classroom.id} value={classroom.id}>
                      {classroom.grade_name} - {classroom.grade_education_level} {classroom.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>CPF do Aluno</Form.Label>
                <Form.Control
                  type="text"
                  name="student_cpf"
                  value={filters.student_cpf}
                  onChange={(e) => {
                    const formatted = formatCpf(e.target.value);
                    setFilters(prev => ({ ...prev, student_cpf: formatted }));
                  }}
                  placeholder="000.000.000-00"
                  maxLength={14}
                  onKeyPress={(e) => e.key === 'Enter' && fetchEnrollments(1)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Nome do Aluno</Form.Label>
                <Form.Control
                  type="text"
                  name="student_name"
                  value={filters.student_name}
                  onChange={handleFilterChange}
                  placeholder="Digite o nome"
                  onKeyPress={(e) => e.key === 'Enter' && fetchEnrollments(1)}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="secondary" onClick={handleClearFilters} className="w-100">
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm">
        <Card.Header className="border-bottom d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Matrículas</h5>
          <Button variant="primary" size="sm" onClick={() => handleOpenModal('create')}>
            <FontAwesomeIcon icon={faUserPlus} className="me-2" />
            Nova Matrícula
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
          ) : enrollments.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Nenhuma matrícula encontrada</p>
            </div>
          ) : (
            <Table responsive hover className="align-middle table-flush mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Matrícula</th>
                  <th>Aluno</th>
                  <th>CPF</th>
                  <th>Turma</th>
                  <th>Ano</th>
                  <th>Data de Matrícula</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map((enrollment) => (
                  <tr key={enrollment.id}>
                    <td>
                      <Badge bg="primary">{enrollment.registration}</Badge>
                    </td>
                    <td>{enrollment.student_name}</td>
                    <td>{enrollment.student_cpf}</td>
                    <td>{getFullClassroomName(enrollment)}</td>
                    <td>{enrollment.year}</td>
                    <td>{new Date(enrollment.registration_date).toLocaleDateString('pt-BR')}</td>
                    <td>
                      <Button
                        variant="info"
                        size="sm"
                        className="me-2"
                        onClick={() => handleOpenModal('edit', enrollment)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(enrollment.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {!loading && enrollments.length > 0 && (
          <Card.Footer className="px-3 border-0 d-flex justify-content-center">
            <Pagination className="mb-0">
              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.previous}
              />
              {[...Array(pagination.totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === pagination.currentPage}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.next}
              />
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} backdrop={modalMode === 'edit' ? 'static' : true} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'Nova Matrícula' : 'Editar Matrícula'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {alert.show && (
            <Alert variant={alert.variant} onClose={() => setAlert({ ...alert, show: false })} dismissible>
              {alert.message}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Busca por CPF ou nome */}
            <Form.Group className="mb-3">
              <Form.Label>CPF ou nome do aluno</Form.Label>
              <Row>
                <Col md={8}>
                  <Form.Control
                    type="text"
                    placeholder="000.000.000-00 ou nome"
                    value={studentSearch}
                    onChange={handleStudentSearchChange}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearchStudent())}
                    disabled={modalMode === 'edit'}
                  />
                </Col>
                <Col md={4}>
                  <Button
                    variant="primary"
                    onClick={handleSearchStudent}
                    disabled={searchingStudent || modalMode === 'edit'}
                    className="w-100"
                  >
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    {searchingStudent ? 'Buscando...' : 'Buscar'}
                  </Button>
                </Col>
              </Row>
            </Form.Group>

            {studentOptions.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Selecione o aluno</Form.Label>
                <Form.Select
                  value={student?.id || ''}
                  onChange={(e) => {
                    const selected = studentOptions.find((item) => String(item.id) === e.target.value);
                    setStudent(selected || null);
                  }}
                >
                  <option value="">Selecione</option>
                  {studentOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}{item.cpf ? ` - ${formatCpf(item.cpf)}` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {/* Informações do Aluno */}
            {student && (
              <Alert variant="success">
                <strong>Aluno:</strong> {student.name}
                {student.cpf ? ` (${formatCpf(student.cpf)})` : ''}
                <br />
                <small className="text-muted">
                  O número de matrícula será gerado automaticamente pelo sistema.
                </small>
              </Alert>
            )}

            {/* Seleção de Turma */}
            <Form.Group className="mb-3">
              <Form.Label>Turma</Form.Label>
              <Form.Select
                value={classroomId}
                onChange={(e) => setClassroomId(e.target.value)}
                required
              >
                <option value="">Selecione uma turma</option>
                {classrooms.map((classroom) => (
                  <option key={classroom.id} value={classroom.id}>
                    {classroom.grade_name} - {classroom.grade_education_level} {classroom.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Ano */}
            <Form.Group className="mb-3">
              <Form.Label>Ano Letivo</Form.Label>
              <Form.Control
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                min="2020"
                max="2099"
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleCloseModal} className="me-2">
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading || !student}>
                {loading ? 'Processando...' : modalMode === 'create' ? 'Matricular' : 'Atualizar'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Enrollments;
